import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    ArrowRight,
    Archive,
    BarChart3,
    Bot,
    CheckCircle2,
    Copy,
    FileText,
    Gift,
    Lightbulb,
    ListChecks,
    MessageCircle,
    MoreHorizontal,
    PackageCheck,
    Paperclip,
    Pencil,
    Pin,
    Plus,
    RefreshCw,
    Search,
    Send,
    SlidersHorizontal,
    Sparkles,
    Tag,
    ThumbsDown,
    ThumbsUp,
    Trash2,
    X,
} from "lucide-react";
import Layout from "../../components/Layout/Layout";
import LoadingState from "../../components/Common/LoadingState";
import { AuthContext } from "../../context";
import { getDashboardSummary } from "../../api/dashboardApi";
import {
    streamAuraMessage,
    getStarterQuestions,
    getAuraSessions,
    getAuraSessionChats,
    renameAuraSession,
    pinAuraSession,
    deleteAuraSession,
    sendAuraFeedback,
    getFeedbackIssueTypes,
} from "../../api/aiagent/auraApi";

const starterMessages = [
    {
        id: "welcome",
        role: "assistant",
        content: "Hi! I'm Aura, your KORRES creator assistant. I can help you understand your content, gifts, orders and next actions.",
    },
];

const defaultStarterQuestions = [
    "What should I focus on today?",
    "Give me an idea for my next post",
    "Summarize my KORRES activity",
];

const QUICK_ACTION_STYLES = [
    { icon: ListChecks, tone: "bg-violet-100 text-violet-600" },
    { icon: Lightbulb, tone: "bg-blue-100 text-blue-600" },
    { icon: Sparkles, tone: "bg-fuchsia-100 text-fuchsia-600" },
];

// Accepts either plain strings (the local fallback) or starter-question API
// items ({ title, description }) — title is the short chip label, description
// (when present) is the fuller question actually sent as the query.
const toQuickActions = (items) =>
    items.map((item, index) => {
        const isPlainText = typeof item === "string";
        const label = isPlainText ? item : (item.title || item.description || "");
        const query = isPlainText ? item : (item.description || item.title || "");
        return { label, query, ...QUICK_ACTION_STYLES[index % QUICK_ACTION_STYLES.length] };
    });

const recommendedActions = [
    { text: "Show my top gift recommendations", icon: Gift, tone: "bg-violet-100 text-violet-600" },
    { text: "Best performing content this month", icon: BarChart3, tone: "bg-blue-100 text-blue-600" },
    { text: "Track my recent orders", icon: Tag, tone: "bg-emerald-100 text-emerald-600" },
];

// Attachments only ever create local blob previews — nothing in the /aiagent
// contract accepts a file, so Aura never actually sees them. Hidden until
// there's a real upload/analysis endpoint to wire this to.
const ATTACHMENTS_ENABLED = false;

const getFirstName = (user) =>
    user?.firstName || user?.FirstName || user?.username || user?.Username || "Creator";

// Same photo/initial pattern as SidebarProfile, so the user's own avatar
// looks consistent across the app.
const getUserPhotoUrl = (user) => user?.profilePhotoUrl || "";
const getUserInitial = (user) => String(getFirstName(user)).trim().charAt(0).toUpperCase() || "K";

const createLocalThread = () => ({
    id: `local-${Date.now()}`,
    title: "New conversation",
    time: "Now",
    group: "Today",
    pinned: false,
    archived: false,
    messageCount: 1,
    aiSessionId: null,
});

const Aura = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const [showPromptIdeas, setShowPromptIdeas] = useState(false);
    const [showConversations, setShowConversations] = useState(false);
    const [conversationSearch, setConversationSearch] = useState("");
    const [openMenuId, setOpenMenuId] = useState(null);
    const [conversationAction, setConversationAction] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [messageFeedback, setMessageFeedback] = useState({});
    const [feedbackDialog, setFeedbackDialog] = useState(null);
    const [feedbackComment, setFeedbackComment] = useState("");
    const [feedbackIssueTypeId, setFeedbackIssueTypeId] = useState("");
    const [feedbackIssueError, setFeedbackIssueError] = useState(false);
    const [issueTypes, setIssueTypes] = useState([]);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [conversationView, setConversationView] = useState("active");
    const [showActivity, setShowActivity] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [conversationSummary, setConversationSummary] = useState("");
    const [showSummary, setShowSummary] = useState(false);
    const [messages, setMessages] = useState(starterMessages);
    const [quickActions, setQuickActions] = useState(() => toQuickActions(defaultStarterQuestions));
    const [threads, setThreads] = useState(() => [createLocalThread()]);
    const [activeThreadId, setActiveThreadId] = useState(() => threads[0].id);
    const messagesEndRef = useRef(null);
    const attachmentInputRef = useRef(null);
    const composerRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        getDashboardSummary()
            .then((data) => {
                if (mounted) setSummary(data);
            })
            .catch(() => {})
            .finally(() => {
                if (mounted) setIsLoading(false);
            });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        getStarterQuestions()
            .then((items) => {
                if (!mounted) return;
                const valid = items.filter((item) => item?.is_active !== false && (item?.title || item?.description));
                if (valid.length) setQuickActions(toQuickActions(valid));
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        getFeedbackIssueTypes()
            .then((items) => {
                if (mounted) setIssueTypes(items);
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        getAuraSessions()
            .then((items) => {
                if (!mounted || !items.length) return;
                setThreads(items);
                setActiveThreadId(items[0].id);
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    const activeAiSessionId = useMemo(
        () => threads.find((thread) => thread.id === activeThreadId)?.aiSessionId || null,
        [threads, activeThreadId]
    );

    // Replaces the message list with a freshly-fetched session history and
    // seeds messageFeedback from each message's server-persisted `feedback`,
    // so thumbs reflect what was actually submitted rather than only the
    // optimistic local guess made at submit time.
    const applySessionMessages = (items) => {
        setMessages(items);
        setMessageFeedback((current) => {
            const next = { ...current };
            items.forEach((item) => {
                if (item.feedback) next[item.id] = item.feedback.is_positive ? "helpful" : "not-helpful";
            });
            return next;
        });
    };

    useEffect(() => {
        if (!activeAiSessionId) {
            setMessages(starterMessages);
            return;
        }
        let mounted = true;
        getAuraSessionChats(activeAiSessionId)
            .then((items) => {
                if (mounted && items.length) applySessionMessages(items);
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, [activeAiSessionId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isThinking]);

    useEffect(() => {
        const handleShortcut = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setShowConversations(true);
            }
            if ((event.ctrlKey || event.metaKey) && event.key === "/") {
                event.preventDefault();
                setShowShortcuts(true);
            }
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "s") {
                event.preventDefault();
                summarizeConversation();
            }
            if (event.key === "Escape") {
                setShowConversations(false);
                setConversationAction(null);
                setShowActivity(false);
                setShowShortcuts(false);
                setShowSummary(false);
                setOpenMenuId(null);
            }
        };
        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    });

    const priorities = useMemo(() => [
        {
            icon: PackageCheck,
            label: "Gift orders",
            value: summary?.ordersCount ?? 0,
            detail: "Track delivery and order progress",
            to: "/gift-orders",
            tone: "border border-emerald-200/80 bg-emerald-100/70 text-emerald-700",
        },
        {
            icon: Gift,
            label: "My gifts",
            value: summary?.giftsCount ?? 0,
            detail: "Review products received from KORRES",
            to: "/my-gifts",
            tone: "border border-fuchsia-200/80 bg-fuchsia-100/70 text-fuchsia-700",
        },
        {
            icon: CheckCircle2,
            label: "Published content",
            value: summary?.contentCount ?? 0,
            detail: "See and manage your submissions",
            to: "/my-content",
            tone: "border border-cyan-200/80 bg-cyan-100/70 text-cyan-700",
        },
    ], [summary]);

    const activeThreads = useMemo(() => threads.filter((thread) => !thread.archived), [threads]);

    const filteredThreads = useMemo(() => {
        const query = conversationSearch.trim().toLowerCase();
        return [...threads]
            .sort((a, b) => Number(b.pinned) - Number(a.pinned))
            .filter((thread) => thread.archived === (conversationView === "archived"))
            .filter((thread) => !query || thread.title.toLowerCase().includes(query));
    }, [conversationSearch, conversationView, threads]);

    const groupedThreads = useMemo(() => ["Today", "Yesterday", "Last 7 days"].map((label) => ({
        label,
        items: filteredThreads.filter((thread) => thread.group === label),
    })).filter((group) => group.items.length), [filteredThreads]);

    const auraFallbackReply = "Sorry, I couldn't reach Aura's assistant service. Please try again in a moment.";

    // Streams one reply for `question` in `threadIdAtSend`, invoking onFirstToken
    // once (create the bubble) then onToken for every token after, and always
    // finishing with onFinal (content/chatId/sessionId, plus whether any tokens
    // streamed at all) or onError if the request/stream itself failed.
    const runAuraStream = async (question, threadIdAtSend, { onFirstToken, onToken, onFinal, onError }) => {
        let started = false;
        try {
            await streamAuraMessage({
                query: question,
                sessionId: threads.find((thread) => thread.id === threadIdAtSend)?.aiSessionId,
                onSessionReady: (sessionId) => {
                    setThreads((current) => current.map((thread) => thread.id === threadIdAtSend ? { ...thread, aiSessionId: sessionId } : thread));
                },
                onDelta: (token) => {
                    if (!started) {
                        started = true;
                        onFirstToken(token);
                    } else {
                        onToken(token);
                    }
                },
                onFinal: (result) => {
                    if (result.sessionId) {
                        setThreads((current) => current.map((thread) => thread.id === threadIdAtSend ? { ...thread, aiSessionId: result.sessionId } : thread));
                    }
                    onFinal(result, started);
                },
            });
        } catch (error) {
            onError(error, started);
        }
    };

    const sendMessage = async (value = draft) => {
        const text = value.trim();
        if ((!text && !attachments.length) || isThinking) return;

        const promptText = text || `Review ${attachments.length} attached file${attachments.length === 1 ? "" : "s"}`;
        const threadIdAtSend = activeThreadId;
        const streamingMsgId = `aura-${Date.now()}`;

        setMessages((current) => editingMessageId
            ? current.map((message) => message.id === editingMessageId ? { ...message, content: promptText, attachments } : message)
            : [...current, { id: `user-${Date.now()}`, role: "user", content: promptText, attachments }]);
        setDraft("");
        setAttachments([]);
        setEditingMessageId(null);
        setShowPromptIdeas(false);
        setIsThinking(true);
        setThreads((current) => current.map((thread) => thread.id === threadIdAtSend ? { ...thread, messageCount: (thread.messageCount || 0) + 2, time: "Now", group: "Today" } : thread));

        await runAuraStream(promptText, threadIdAtSend, {
            onFirstToken: (token) => {
                setIsThinking(false);
                setMessages((current) => [...current, { id: streamingMsgId, role: "assistant", content: token, streaming: true }]);
            },
            onToken: (token) => {
                setMessages((current) => current.map((message) => message.id === streamingMsgId ? { ...message, content: message.content + token } : message));
            },
            onFinal: (result, started) => {
                setIsThinking(false);
                if (started) {
                    setMessages((current) => current.map((message) => message.id === streamingMsgId
                        ? { ...message, content: result.content || message.content, chatId: result.chatId, sessionId: result.sessionId, streaming: false }
                        : message));
                } else {
                    setMessages((current) => [...current, { id: streamingMsgId, role: "assistant", content: result.content || auraFallbackReply, chatId: result.chatId, sessionId: result.sessionId }]);
                }
            },
            onError: () => {
                setIsThinking(false);
                setMessages((current) => [
                    ...current.filter((message) => message.id !== streamingMsgId),
                    { id: `aura-error-${Date.now()}`, role: "assistant", content: auraFallbackReply },
                ]);
            },
        });
    };

    const startNewChat = () => {
        // A thread with no aiSessionId hasn't had a first message sent yet — if
        // one already exists (e.g. the user clicked "New conversation" twice in
        // a row without typing anything), reuse it instead of stacking up empty
        // drafts, matching ChatGPT's "New chat" behavior.
        const existingDraft = threads.find((thread) => !thread.aiSessionId && !thread.archived);
        if (existingDraft) {
            setActiveThreadId(existingDraft.id);
            setMessages(starterMessages);
            setDraft("");
            setShowPromptIdeas(false);
            return;
        }

        const thread = createLocalThread();
        setThreads((current) => [thread, ...current]);
        setActiveThreadId(thread.id);
        setMessages(starterMessages);
        setDraft("");
        setShowPromptIdeas(false);
    };

    const archiveConversation = (thread) => {
        setThreads((current) => current.map((item) => item.id === thread.id ? { ...item, archived: !item.archived, pinned: false } : item));
        setOpenMenuId(null);
    };

    const summarizeConversation = () => {
        const userMessages = messages.filter((message) => message.role === "user");
        const topics = userMessages.slice(-3).map((message) => message.content).join("; ");
        setConversationSummary(userMessages.length
            ? `You discussed ${topics}. The key next step is to review the relevant KORRES content, gifts, or orders and follow up on any outstanding action.`
            : "This conversation currently contains only Aura's welcome message. Ask a question and I can summarize the discussion afterward.");
        setShowSummary(true);
    };

    const regenerateMessage = async (messageId) => {
        const index = messages.findIndex((message) => message.id === messageId);
        const previousPrompt = [...messages.slice(0, index)].reverse().find((message) => message.role === "user");
        if (!previousPrompt || isThinking) return;

        setIsThinking(true);
        await runAuraStream(previousPrompt.content, activeThreadId, {
            onFirstToken: (token) => {
                setIsThinking(false);
                setMessages((current) => current.map((message) => message.id === messageId ? { ...message, content: token, streaming: true, regenerated: true } : message));
            },
            onToken: (token) => {
                setMessages((current) => current.map((message) => message.id === messageId ? { ...message, content: message.content + token } : message));
            },
            onFinal: (result, started) => {
                setIsThinking(false);
                setMessages((current) => current.map((message) => message.id === messageId
                    ? { ...message, content: result.content || (started ? message.content : auraFallbackReply), chatId: result.chatId, sessionId: result.sessionId, streaming: false, regenerated: true }
                    : message));
            },
            onError: () => {
                setIsThinking(false);
                setMessages((current) => current.map((message) => message.id === messageId ? { ...message, content: auraFallbackReply, streaming: false, regenerated: true } : message));
            },
        });
    };

    const sendMessageFeedback = (message, isPositive) => {
        setFeedbackDialog({ message, isPositive });
        setFeedbackComment("");
        setFeedbackIssueTypeId("");
        setFeedbackIssueError(false);
    };

    const closeFeedbackDialog = () => {
        setFeedbackDialog(null);
        setFeedbackComment("");
        setFeedbackIssueTypeId("");
        setFeedbackIssueError(false);
    };

    const goToDetailedSurvey = () => {
        if (!feedbackDialog) return;
        const { message } = feedbackDialog;
        const params = new URLSearchParams();
        if (message.sessionId) params.set("session_id", message.sessionId);
        if (message.chatId) params.set("chat_id", message.chatId);
        closeFeedbackDialog();
        navigate(`/aura-ai-assistant/feedback?${params.toString()}`);
    };

    const submitFeedbackDialog = async () => {
        if (!feedbackDialog) return;
        const { message, isPositive } = feedbackDialog;
        if (!isPositive && !feedbackIssueTypeId) {
            setFeedbackIssueError(true);
            return;
        }
        setIsSubmittingFeedback(true);
        setMessageFeedback((current) => ({ ...current, [message.id]: isPositive ? "helpful" : "not-helpful" }));
        try {
            if (message.chatId && message.sessionId) {
                await sendAuraFeedback({
                    chatId: message.chatId,
                    sessionId: message.sessionId,
                    isPositive,
                    issueTypeId: isPositive ? null : feedbackIssueTypeId,
                    comment: feedbackComment,
                });

                // Re-fetch so the thumb reflects what the server actually
                // persisted — but only if the user is still viewing this
                // conversation (they may have switched threads by now).
                if (message.sessionId === activeAiSessionId) {
                    try {
                        const items = await getAuraSessionChats(message.sessionId);
                        if (items.length) applySessionMessages(items);
                    } catch {
                        // best-effort; the optimistic thumb state above still stands
                    }
                }
            }
        } catch {
            // best-effort; the thumb icon already reflects the user's choice
        } finally {
            setIsSubmittingFeedback(false);
            closeFeedbackDialog();
        }
    };

    const editPrompt = (message) => {
        setDraft(message.content);
        setAttachments(message.attachments || []);
        setEditingMessageId(message.id);
        composerRef.current?.focus();
    };

    const addAttachments = (event) => {
        const selected = Array.from(event.target.files || []).map((file) => ({
            id: `${file.name}-${file.lastModified}`,
            name: file.name,
            size: file.size,
            type: file.type,
            url: URL.createObjectURL(file),
        }));
        setAttachments((current) => [...current, ...selected].slice(0, 5));
        event.target.value = "";
    };

    const openConversationAction = (type, thread) => {
        setOpenMenuId(null);
        setConversationAction({ type, thread });
        setRenameValue(thread.title);
    };

    const togglePin = async (thread) => {
        const nextPinned = !thread.pinned;
        setThreads((current) => current.map((item) => item.id === thread.id ? { ...item, pinned: nextPinned } : item));
        setOpenMenuId(null);
        if (thread.aiSessionId) {
            try {
                await pinAuraSession(thread.aiSessionId, nextPinned);
            } catch {
                // best-effort; the sidebar already reflects the user's choice
            }
        }
    };

    const confirmConversationAction = async () => {
        if (!conversationAction) return;
        const { type, thread } = conversationAction;

        if (type === "rename" && renameValue.trim()) {
            const title = renameValue.trim();
            setThreads((current) => current.map((item) => item.id === thread.id ? { ...item, title } : item));
            if (thread.aiSessionId) {
                try {
                    await renameAuraSession(thread.aiSessionId, title);
                } catch {
                    // best-effort; the sidebar already reflects the new title
                }
            }
        }
        if (type === "delete") {
            const remaining = threads.filter((item) => item.id !== thread.id);
            if (activeThreadId === thread.id) {
                const fallback = createLocalThread();
                setThreads([fallback, ...remaining]);
                setActiveThreadId(fallback.id);
            } else {
                setThreads(remaining);
            }
            if (thread.aiSessionId) {
                try {
                    await deleteAuraSession(thread.aiSessionId);
                } catch {
                    // best-effort; the sidebar already reflects the removal
                }
            }
        }
        setConversationAction(null);
    };

    if (isLoading) {
        return (
            <Layout activeLabel="Aura">
                <LoadingState message="Preparing Aura..." fullScreen />
            </Layout>
        );
    }

    return (
        <Layout activeLabel="Aura">
            <div className="relative p-2 isolate mx-auto h-[calc(100vh-73px)] overflow-hidden bg-gradient-to-br from-[#f3f6ff] via-[#f0edff] to-[#fff3fa] text-slate-800 md:h-screen">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_14%,rgba(139,92,246,0.36),transparent_30%),radial-gradient(circle_at_86%_8%,rgba(34,211,238,0.30),transparent_27%),radial-gradient(circle_at_74%_90%,rgba(244,114,182,0.28),transparent_30%)]" />
                <div className="aura-ambient pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-fuchsia-300/25 blur-3xl" />
                <div className="aura-ambient aura-ambient-delay pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-cyan-300/25 blur-3xl" />
                <div className="aura-ambient pointer-events-none absolute bottom-0 left-[42%] h-72 w-72 rounded-full bg-violet-300/25 blur-3xl" />
                <div className="relative grid h-full min-h-0 gap-3 lg:grid-cols-[290px_minmax(0,1fr)] xl:grid-cols-[290px_minmax(0,1fr)_370px]">
                    <aside className="aura-panel-reveal hidden overflow-hidden rounded-[24px] border border-white/90 bg-white/35 p-4 shadow-[0_18px_55px_rgba(79,70,229,0.08)] backdrop-blur-2xl lg:flex lg:flex-col">
                        <div className="flex items-center gap-3 px-2">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/80 bg-white/45 shadow-[0_8px_24px_rgba(124,58,237,0.16)] backdrop-blur-xl">
                                <img src="/assets/images/aura/aura-icon.png" alt="" className="aura-logo-motion h-10 w-10 object-contain" />
                            </div>
                            <div>
                                <h1 className="text-base font-semibold text-slate-900">Aura</h1>
                                <p className="text-xs text-slate-500">KORRES creator assistant</p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={startNewChat}
                            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-300/30 bg-gradient-to-r from-violet-600 to-cyan-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(124,58,237,0.28)] transition hover:brightness-110"
                        >
                            <Plus className="h-4 w-4" />
                            New conversation
                        </button>

                        <p className="mt-5 px-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Recent</p>
                        <div className="mt-2 space-y-2">
                            {activeThreads.slice(0, 4).map((thread) => (
                                <div
                                    key={thread.id}
                                    className={`group relative flex items-center rounded-xl transition ${
                                    activeThreadId === thread.id ? "border border-white/90 bg-white/55 shadow-[0_8px_24px_rgba(124,58,237,0.12)] backdrop-blur-xl" : "border border-transparent hover:bg-white/35"
                                    }`}
                                >
                                    <button type="button" onClick={() => setActiveThreadId(thread.id)} className="min-w-0 flex-1 px-3 py-3 text-left">
                                        <span className="flex items-center gap-1.5">
                                            <span className="block truncate text-sm font-medium text-slate-700">{thread.title}</span>
                                            {thread.pinned ? <Pin className="h-3 w-3 shrink-0 fill-violet-500 text-violet-500" /> : null}
                                        </span>
                                        <span className="mt-1 block text-xs text-slate-400">{thread.time}</span>
                                    </button>
                                    <button type="button" onClick={() => setOpenMenuId(openMenuId === thread.id ? null : thread.id)} className="mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 opacity-0 transition hover:bg-white/70 hover:text-slate-700 group-hover:opacity-100 focus:opacity-100" aria-label={`Actions for ${thread.title}`}>
                                        <MoreHorizontal className="h-4 w-4" />
                                    </button>
                                    {openMenuId === thread.id ? (
                                        <div className="absolute right-2 top-11 z-20 w-36 rounded-xl border border-white/90 bg-white/95 p-1.5 shadow-xl backdrop-blur-xl">
                                            <button type="button" onClick={() => togglePin(thread)} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-slate-600 hover:bg-violet-50"><Pin className="h-3.5 w-3.5" />{thread.pinned ? "Unpin" : "Pin"}</button>
                                            <button type="button" onClick={() => openConversationAction("rename", thread)} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-slate-600 hover:bg-violet-50"><Pencil className="h-3.5 w-3.5" />Rename</button>
                                            <button type="button" onClick={() => archiveConversation(thread)} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-slate-600 hover:bg-violet-50"><Archive className="h-3.5 w-3.5" />Archive</button>
                                            <button type="button" onClick={() => { setShowActivity(true); setOpenMenuId(null); }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-slate-600 hover:bg-violet-50"><BarChart3 className="h-3.5 w-3.5" />Activity</button>
                                            <button type="button" onClick={() => openConversationAction("delete", thread)} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" />Delete</button>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                        {activeThreads.length > 4 ? (
                            <button type="button" onClick={() => setShowConversations(true)} className="mt-3 inline-flex items-center justify-center gap-2 text-xs font-medium text-slate-500 hover:text-violet-600">
                                View all conversations <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                        ) : null}

                        <div className="mt-auto rounded-xl border border-white/80 bg-white/40 p-4 shadow-sm backdrop-blur-xl">
                            <p className="text-xs font-semibold text-violet-700">Live assistant</p>
                            <p className="mt-1 text-xs leading-relaxed text-slate-500">Replies come from Aura in real time. Streaming responses can be connected next; conversation history is currently kept in this browser only.</p>
                        </div>
                    </aside>

                    <section className="aura-panel-reveal aura-panel-delay-1 flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-[24px] border border-white/90 bg-white/25 shadow-[0_18px_55px_rgba(79,70,229,0.08)] backdrop-blur-md">
                        <header className="flex items-center justify-between border-b border-white/70 bg-white/30 px-5 py-4 backdrop-blur-xl sm:px-6">
                            <div>
                                <div className="flex items-center gap-2">
                                    <img src="/assets/images/aura/aura-icon.png" alt="Aura" className="h-8 w-8 object-contain" />
                                    <p className="text-[22px] font-semibold tracking-tight text-slate-900">Creator overview</p>
                                </div>
                                <p className="mt-1 text-xs text-slate-500">Personalized for {getFirstName(user)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={summarizeConversation} className="hidden items-center gap-2 rounded-xl border border-white/90 bg-white/55 px-3 py-2 text-xs font-medium text-violet-600 shadow-sm transition hover:bg-white/80 sm:flex" title="Summarize conversation (Ctrl+Shift+S)"><Sparkles className="h-4 w-4" />Summarize</button>
                                <button
                                    type="button"
                                    onClick={() => setShowPromptIdeas((current) => !current)}
                                    className={`flex h-10 w-10 items-center justify-center rounded-xl border text-slate-600 shadow-sm backdrop-blur-xl transition ${showPromptIdeas ? "border-violet-300 bg-white/85 ring-2 ring-violet-200/60" : "border-white/90 bg-white/55 hover:bg-white/80"}`}
                                    aria-label={showPromptIdeas ? "Hide Aura actions" : "Show Aura actions"}
                                    aria-expanded={showPromptIdeas}
                                >
                                    <SlidersHorizontal className="h-4 w-4" />
                                </button>
                            </div>
                        </header>

                        <div className="aura-chat-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                            <div className="mx-auto max-w-[720px] space-y-6">
                                {messages.map((message) => (
                                    <div
                                        key={message.id}
                                        className={`aura-message-reveal flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                                    >
                                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                                            message.role === "assistant"
                                                ? "border border-violet-200/70 bg-white/50 text-violet-700 shadow-[0_8px_20px_rgba(124,58,237,0.12)]"
                                                : "border border-white/80 bg-white/45 text-slate-600"
                                        }`}>
                                            {message.role === "assistant" ? (
                                                <img
                                                    src="/assets/images/aura/aura-icon.png"
                                                    alt="Aura"
                                                    className="aura-logo-motion h-8 w-8 object-contain"
                                                />
                                            ) : getUserPhotoUrl(user) ? (
                                                <img src={getUserPhotoUrl(user)} alt={getFirstName(user)} className="h-full w-full rounded-full object-cover" />
                                            ) : (
                                                <span className="text-xs font-semibold">{getUserInitial(user)}</span>
                                            )}
                                        </div>
                                        <div className={`max-w-[510px] ${message.role === "user" ? "text-right" : ""}`}>
                                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                                {message.role === "assistant" ? "Aura" : "You"}
                                            </p>
                                            <div className={`rounded-[22px] px-5 py-4 text-left text-[15px] leading-7 ${
                                                message.role === "assistant"
                                                    ? "rounded-tl-md border border-white/85 bg-white/55 text-slate-700 shadow-[0_12px_30px_rgba(79,70,229,0.09)] backdrop-blur-xl"
                                                    : "rounded-tr-md border border-cyan-300/20 bg-gradient-to-br from-violet-600/90 to-fuchsia-600/80 text-white shadow-[0_10px_28px_rgba(124,58,237,0.22)]"
                                            }`}>
                                                {message.role === "assistant" ? (
                                                    <div className="prose prose-sm prose-slate max-w-none prose-p:my-2 prose-headings:mb-2 prose-headings:mt-3 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-2 prose-strong:text-slate-900 prose-a:text-violet-600">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                                                    </div>
                                                ) : message.content}
                                                {message.attachments?.length ? (
                                                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                                        {message.attachments.map((attachment) => attachment.type?.startsWith("image/") ? (
                                                            <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border border-white/40 bg-white/20">
                                                                <img src={attachment.url} alt={attachment.name} className="h-24 w-full object-cover" />
                                                                <span className="block truncate px-2 py-1 text-[11px]">{attachment.name}</span>
                                                            </a>
                                                        ) : (
                                                            <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-white/40 bg-white/20 p-2 text-xs">
                                                                <FileText className="h-4 w-4 shrink-0" /><span className="truncate">{attachment.name}</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                            {message.role === "assistant" ? (
                                                <div className="mt-2 flex items-center gap-1 text-slate-400">
                                                    <button type="button" onClick={() => navigator.clipboard?.writeText(message.content)} className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs hover:bg-white/50 hover:text-violet-600"><Copy className="h-3.5 w-3.5" /> Copy</button>
                                                    <button type="button" onClick={() => regenerateMessage(message.id)} className="rounded-md p-1.5 hover:bg-white/50 hover:text-violet-600" aria-label="Regenerate response" title="Regenerate"><RefreshCw className="h-3.5 w-3.5" /></button>
                                                    <button type="button" onClick={() => sendMessageFeedback(message, true)} className={`rounded-md p-1.5 hover:bg-white/50 hover:text-emerald-600 ${messageFeedback[message.id] === "helpful" ? "text-emerald-600" : ""}`} aria-label="Helpful response"><ThumbsUp className="h-3.5 w-3.5" /></button>
                                                    <button type="button" onClick={() => sendMessageFeedback(message, false)} className={`rounded-md p-1.5 hover:bg-white/50 hover:text-rose-600 ${messageFeedback[message.id] === "not-helpful" ? "text-rose-600" : ""}`} aria-label="Unhelpful response"><ThumbsDown className="h-3.5 w-3.5" /></button>
                                                </div>
                                            ) : (
                                                <button type="button" onClick={() => editPrompt(message)} className="mt-2 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-slate-400 hover:bg-white/50 hover:text-violet-600"><Pencil className="h-3.5 w-3.5" /> Edit prompt</button>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {isThinking ? (
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-violet-200/70 bg-white/50 shadow-[0_8px_20px_rgba(124,58,237,0.12)]">
                                            <img
                                                src="/assets/images/aura/aura-icon.png"
                                                alt="Aura"
                                                className="aura-logo-motion h-8 w-8 object-contain"
                                            />
                                        </div>
                                        <div className="flex items-center gap-1 rounded-2xl rounded-tl-md border border-white/85 bg-white/55 px-4 py-4 shadow-sm backdrop-blur-xl">
                                            {[0, 1, 2].map((dot) => <span key={dot} className="aura-thinking-dot h-1.5 w-1.5 rounded-full bg-indigo-400" />)}
                                        </div>
                                    </div>
                                ) : null}

                                {showPromptIdeas && !isThinking ? (
                                    <div className="space-y-4 pt-4">
                                        <div className="rounded-[22px] border border-white/80 bg-white/25 p-4 backdrop-blur-xl">
                                            <p className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-700">
                                                <span className="text-violet-500">✦</span> Quick actions
                                            </p>
                                            <div className="grid gap-3 md:grid-cols-3">
                                                {quickActions.map((action) => {
                                                    const Icon = action.icon;
                                                    return (
                                                        <button key={action.label} type="button" onClick={() => sendMessage(action.query)} className="group flex min-h-[82px] items-center gap-3 rounded-xl border border-white/90 bg-white/55 p-3 text-left shadow-sm backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/75">
                                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${action.tone}`}><Icon className="h-4 w-4" /></span>
                                                            <span className="text-xs font-medium leading-5 text-slate-700">{action.label}</span>
                                                            <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5" />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="rounded-[22px] border border-white/80 bg-white/25 p-4 backdrop-blur-xl">
                                            <p className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-700">
                                                <span className="text-blue-500">✧</span> Suggested for you
                                            </p>
                                            <div className="grid gap-3 md:grid-cols-3">
                                                {recommendedActions.map((action) => {
                                                    const Icon = action.icon;
                                                    return (
                                                        <button key={action.text} type="button" onClick={() => sendMessage(action.text)} className="group flex min-h-[76px] items-center gap-3 rounded-xl border border-white/90 bg-white/55 p-3 text-left shadow-sm backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/75">
                                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${action.tone}`}><Icon className="h-4 w-4" /></span>
                                                            <span className="text-xs font-medium leading-5 text-slate-700">{action.text}</span>
                                                            <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5" />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        <div className="border-t border-white/70 bg-white/30 px-5 py-4 backdrop-blur-2xl sm:px-6">
                            <div className="mx-auto max-w-[720px]">
                                {editingMessageId ? <div className="mb-2 flex items-center justify-between rounded-lg bg-violet-50/80 px-3 py-2 text-xs text-violet-700"><span>Editing your prompt</span><button type="button" onClick={() => { setEditingMessageId(null); setDraft(""); setAttachments([]); }} className="font-semibold">Cancel</button></div> : null}
                                {attachments.length ? (
                                    <div className="mb-2 flex flex-wrap gap-2">
                                        {attachments.map((attachment) => (
                                            <div key={attachment.id} className="flex max-w-[190px] items-center gap-2 rounded-xl border border-white/90 bg-white/70 px-2.5 py-2 text-xs text-slate-600 shadow-sm">
                                                <FileText className="h-4 w-4 shrink-0 text-violet-500" /><span className="truncate">{attachment.name}</span>
                                                <button type="button" onClick={() => setAttachments((current) => current.filter((item) => item.id !== attachment.id))} className="shrink-0 text-slate-400 hover:text-rose-500" aria-label={`Remove ${attachment.name}`}><X className="h-3.5 w-3.5" /></button>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                                <div className="flex items-end gap-3 rounded-2xl border border-white/90 bg-white/60 p-3 shadow-[0_16px_40px_rgba(79,70,229,0.12)] backdrop-blur-xl focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-200/50">
                                    {ATTACHMENTS_ENABLED ? (
                                        <>
                                            <input ref={attachmentInputRef} type="file" multiple className="hidden" onChange={addAttachments} accept="image/*,.pdf,.doc,.docx,.txt" />
                                            <button type="button" onClick={() => attachmentInputRef.current?.click()} className="mb-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-violet-50 hover:text-violet-600" aria-label="Attach files" title="Attach up to 5 files"><Paperclip className="h-4 w-4" /></button>
                                        </>
                                    ) : null}
                                    <textarea
                                        ref={composerRef}
                                        value={draft}
                                        onChange={(event) => setDraft(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" && !event.shiftKey) {
                                                event.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        rows={1}
                                        placeholder="Ask Aura about content, gifts, orders or your profile..."
                                        className="max-h-32 min-h-[52px] flex-1 resize-none bg-transparent px-3 py-4 text-sm text-slate-700 outline-none placeholder:text-slate-400"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => sendMessage()}
                                        disabled={(!draft.trim() && !attachments.length) || isThinking}
                                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-cyan-500 text-white shadow disabled:cursor-not-allowed disabled:opacity-40"
                                        aria-label="Send message"
                                    >
                                        <Send className="h-4 w-4" />
                                    </button>
                                </div>
                                <p className="mt-2 text-center text-[11px] text-slate-400">Aura can make mistakes. Review important information before taking action. Press Ctrl+/ for shortcuts.</p>
                            </div>
                        </div>
                    </section>

                    <aside className="aura-panel-reveal aura-panel-delay-2 hidden overflow-hidden rounded-[24px] border border-white/90 bg-white/35 p-4 shadow-[0_18px_55px_rgba(79,70,229,0.08)] backdrop-blur-2xl xl:block">
                        <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-violet-600" />
                            <h2 className="text-sm font-semibold text-slate-900">Your KORRES snapshot</h2>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-slate-500">Aura uses this context to make answers more relevant.</p>

                        <div className="mt-5 space-y-3">
                            {priorities.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link key={item.label} to={item.to} className="group block rounded-[20px] border border-white/85 bg-white/50 p-4 shadow-sm backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-violet-200 hover:bg-white/70 hover:shadow-[0_14px_35px_rgba(79,70,229,0.1)]">
                                        <div className="flex items-center justify-between">
                                            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${item.tone}`}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <span className="text-xl font-semibold text-slate-900">{item.value}</span>
                                        </div>
                                        <p className="mt-3 text-sm font-semibold text-slate-700">{item.label}</p>
                                        <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.detail}</p>
                                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-violet-600">
                                            Open <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>

                        <Link to="/messages" className="mt-5 flex items-center gap-3 rounded-[20px] border border-violet-300/20 bg-gradient-to-r from-violet-600/80 to-fuchsia-600/70 p-4 text-white shadow-[0_12px_30px_rgba(124,58,237,0.18)] transition hover:brightness-110">
                            <MessageCircle className="h-5 w-5" />
                            <div>
                                <p className="text-sm font-semibold">KORRES support</p>
                                <p className="mt-0.5 text-xs text-white/70">Open your support messages</p>
                            </div>
                            <ArrowRight className="ml-auto h-4 w-4" />
                        </Link>
                    </aside>
                </div>
            </div>

            {showConversations ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/25 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="conversations-title">
                    <div className="w-full max-w-2xl overflow-hidden rounded-[24px] border border-white/90 bg-white/90 shadow-[0_30px_90px_rgba(49,46,129,0.22)] backdrop-blur-2xl">
                        <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4">
                            <div>
                                <h2 id="conversations-title" className="text-lg font-semibold text-slate-900">All conversations</h2>
                                <p className="mt-0.5 text-xs text-slate-500">Search and manage your Aura history</p>
                            </div>
                            <button type="button" onClick={() => setShowConversations(false)} className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100" aria-label="Close conversations"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-5">
                            <div className="mb-4 flex rounded-xl bg-slate-100/80 p-1">
                                {[{ id: "active", label: "Active" }, { id: "archived", label: "Archived" }].map((view) => (
                                    <button key={view.id} type="button" onClick={() => setConversationView(view.id)} className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition ${conversationView === view.id ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{view.label}</button>
                                ))}
                            </div>
                            <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100">
                                <Search className="h-4 w-4 text-slate-400" />
                                <input value={conversationSearch} onChange={(event) => setConversationSearch(event.target.value)} autoFocus placeholder="Search conversations..." className="min-w-0 flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400" />
                            </label>
                            <div className="mt-4 max-h-[420px] space-y-5 overflow-y-auto pr-1">
                                {groupedThreads.length ? groupedThreads.map((group) => (
                                    <section key={group.label}>
                                        <h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{group.label}</h3>
                                        <div className="space-y-2">
                                        {group.items.map((thread) => <div key={thread.id} className="flex items-center gap-3 rounded-xl border border-slate-200/70 bg-white/70 p-3 hover:border-violet-200">
                                        <button type="button" onClick={() => { setActiveThreadId(thread.id); setShowConversations(false); }} className="min-w-0 flex-1 text-left">
                                            <span className="flex items-center gap-2 text-sm font-medium text-slate-700">{thread.pinned ? <Pin className="h-3.5 w-3.5 fill-violet-500 text-violet-500" /> : null}<span className="truncate">{thread.title}</span></span>
                                            <span className="mt-1 block text-xs text-slate-400">{thread.time} · {thread.messageCount || 0} messages</span>
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {!thread.archived ? <button type="button" onClick={() => togglePin(thread)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-violet-50 hover:text-violet-600" aria-label={thread.pinned ? "Unpin conversation" : "Pin conversation"}><Pin className="h-4 w-4" /></button> : null}
                                            <button type="button" onClick={() => archiveConversation(thread)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-violet-50 hover:text-violet-600" aria-label={thread.archived ? "Restore conversation" : "Archive conversation"}><Archive className="h-4 w-4" /></button>
                                            <button type="button" onClick={() => openConversationAction("rename", thread)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-violet-50 hover:text-violet-600" aria-label="Rename conversation"><Pencil className="h-4 w-4" /></button>
                                            <button type="button" onClick={() => openConversationAction("delete", thread)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label="Delete conversation"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </div>)}
                                        </div>
                                    </section>
                                )) : <div className="py-12 text-center text-sm text-slate-400">No {conversationView} conversations found.</div>}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {showSummary ? (
                <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="summary-title">
                    <div className="w-full max-w-lg overflow-hidden rounded-[24px] border border-white/90 bg-gradient-to-br from-white/95 via-violet-50/95 to-fuchsia-50/90 shadow-[0_30px_90px_rgba(79,70,229,0.25)] backdrop-blur-2xl">
                        <div className="flex items-start justify-between border-b border-white/80 px-5 py-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-600"><Sparkles className="h-5 w-5" /></div>
                                <div><h2 id="summary-title" className="text-lg font-semibold text-slate-900">Conversation summary</h2><p className="mt-0.5 text-xs text-slate-500">Generated by Aura from this conversation</p></div>
                            </div>
                            <button type="button" onClick={() => setShowSummary(false)} className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 hover:bg-white/80 hover:text-slate-700" aria-label="Close summary"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-5">
                            <div className="rounded-2xl border border-white/90 bg-white/65 p-5 text-sm leading-7 text-slate-700 shadow-sm">{conversationSummary}</div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                                <div className="rounded-xl bg-white/55 p-3"><p className="text-lg font-semibold text-slate-900">{messages.length}</p><p className="text-[11px] text-slate-500">Messages</p></div>
                                <div className="rounded-xl bg-white/55 p-3"><p className="text-lg font-semibold text-slate-900">{messages.filter((message) => message.role === "user").length}</p><p className="text-[11px] text-slate-500">Prompts</p></div>
                                <div className="rounded-xl bg-white/55 p-3"><p className="text-lg font-semibold text-slate-900">{messages.reduce((total, message) => total + (message.attachments?.length || 0), 0)}</p><p className="text-[11px] text-slate-500">Attachments</p></div>
                            </div>
                            <div className="mt-5 flex justify-end gap-2">
                                <button type="button" onClick={() => navigator.clipboard?.writeText(conversationSummary)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/70 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-white"><Copy className="h-4 w-4" />Copy</button>
                                <button type="button" onClick={() => setShowSummary(false)} className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">Done</button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {showActivity ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="activity-title">
                    <div className="w-full max-w-md rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_30px_80px_rgba(49,46,129,0.24)]">
                        <div className="flex items-start justify-between">
                            <div><h2 id="activity-title" className="text-lg font-semibold text-slate-900">Conversation activity</h2><p className="mt-1 text-sm text-slate-500">Usage details for this Aura session</p></div>
                            <button type="button" onClick={() => setShowActivity(false)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            <div className="rounded-xl bg-violet-50 p-4"><p className="text-xs text-slate-500">Messages</p><p className="mt-1 text-xl font-semibold text-slate-900">{messages.length}</p></div>
                            <div className="rounded-xl bg-cyan-50 p-4"><p className="text-xs text-slate-500">Attachments</p><p className="mt-1 text-xl font-semibold text-slate-900">{messages.reduce((total, message) => total + (message.attachments?.length || 0), 0)}</p></div>
                            <div className="rounded-xl bg-fuchsia-50 p-4"><p className="text-xs text-slate-500">Your prompts</p><p className="mt-1 text-xl font-semibold text-slate-900">{messages.filter((message) => message.role === "user").length}</p></div>
                            <div className="rounded-xl bg-emerald-50 p-4"><p className="text-xs text-slate-500">Aura replies</p><p className="mt-1 text-xl font-semibold text-slate-900">{messages.filter((message) => message.role === "assistant").length}</p></div>
                        </div>
                        <div className="mt-4 rounded-xl border border-slate-200 p-4 text-sm text-slate-600"><div className="flex justify-between"><span>Started</span><span className="font-medium text-slate-800">Today</span></div><div className="mt-3 flex justify-between"><span>Last activity</span><span className="font-medium text-slate-800">Now</span></div><div className="mt-3 flex justify-between"><span>Status</span><span className="font-medium text-emerald-600">Active</span></div></div>
                    </div>
                </div>
            ) : null}

            {showShortcuts ? (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="shortcuts-title">
                    <div className="w-full max-w-sm rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_30px_80px_rgba(49,46,129,0.24)]">
                        <div className="flex items-center justify-between"><h2 id="shortcuts-title" className="text-lg font-semibold text-slate-900">Keyboard shortcuts</h2><button type="button" onClick={() => setShowShortcuts(false)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button></div>
                        <div className="mt-5 space-y-3 text-sm text-slate-600">
                            <div className="flex items-center justify-between"><span>Search conversations</span><kbd className="rounded-md border bg-slate-50 px-2 py-1 text-xs">Ctrl K</kbd></div>
                            <div className="flex items-center justify-between"><span>Summarize conversation</span><kbd className="rounded-md border bg-slate-50 px-2 py-1 text-xs">Ctrl Shift S</kbd></div>
                            <div className="flex items-center justify-between"><span>Send message</span><kbd className="rounded-md border bg-slate-50 px-2 py-1 text-xs">Enter</kbd></div>
                            <div className="flex items-center justify-between"><span>New line</span><kbd className="rounded-md border bg-slate-50 px-2 py-1 text-xs">Shift Enter</kbd></div>
                            <div className="flex items-center justify-between"><span>Close dialog</span><kbd className="rounded-md border bg-slate-50 px-2 py-1 text-xs">Esc</kbd></div>
                        </div>
                    </div>
                </div>
            ) : null}

            {conversationAction ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
                    <div className="w-full max-w-md rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_30px_80px_rgba(49,46,129,0.24)] backdrop-blur-2xl">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">{conversationAction.type === "rename" ? "Rename conversation" : "Delete conversation?"}</h2>
                                <p className="mt-1 text-sm text-slate-500">{conversationAction.type === "delete" ? `“${conversationAction.thread.title}” will be permanently removed.` : "Choose a clear name you can find later."}</p>
                            </div>
                            <button type="button" onClick={() => setConversationAction(null)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button>
                        </div>

                        {conversationAction.type === "rename" ? <input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") confirmConversationAction(); }} autoFocus className="mt-5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100" maxLength={80} /> : null}

                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" onClick={() => setConversationAction(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                            <button type="button" onClick={confirmConversationAction} disabled={conversationAction.type === "rename" && !renameValue.trim()} className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40 ${conversationAction.type === "delete" ? "bg-rose-600 hover:bg-rose-700" : "bg-violet-600 hover:bg-violet-700"}`}>{conversationAction.type === "rename" ? "Save name" : "Delete"}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {feedbackDialog ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="message-feedback-title">
                    <div className="w-full max-w-md rounded-[22px] border border-white/90 bg-white/95 p-5 shadow-[0_30px_80px_rgba(49,46,129,0.24)] backdrop-blur-2xl">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 id="message-feedback-title" className="text-lg font-semibold text-slate-900">{feedbackDialog.isPositive ? "Glad that helped!" : "What went wrong?"}</h2>
                                <p className="mt-1 text-sm text-slate-500">{feedbackDialog.isPositive ? "Add a comment to tell us more (optional)." : "Help us improve this response."}</p>
                            </div>
                            <button type="button" onClick={closeFeedbackDialog} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button>
                        </div>

                        <div className="mt-5 space-y-3">
                            {!feedbackDialog.isPositive ? (
                                <div>
                                    <label htmlFor="feedback-issue-type" className="mb-1.5 block text-xs font-semibold text-slate-600">
                                        Reason <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        id="feedback-issue-type"
                                        value={feedbackIssueTypeId}
                                        onChange={(event) => { setFeedbackIssueTypeId(event.target.value); setFeedbackIssueError(false); }}
                                        className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 ${feedbackIssueError ? "border-rose-400" : "border-slate-200"}`}
                                    >
                                        <option value="">Select a reason…</option>
                                        {issueTypes.map((issueType) => (
                                            <option key={issueType.id} value={issueType.id}>{issueType.label}</option>
                                        ))}
                                    </select>
                                    {feedbackIssueError ? <p className="mt-1 text-xs text-rose-500">Please select a reason.</p> : null}
                                </div>
                            ) : null}
                            <div>
                                <label htmlFor="feedback-comment" className="mb-1.5 block text-xs font-semibold text-slate-600">Comment (optional)</label>
                                <textarea
                                    id="feedback-comment"
                                    value={feedbackComment}
                                    onChange={(event) => setFeedbackComment(event.target.value)}
                                    rows={4}
                                    placeholder={feedbackDialog.isPositive ? "What did you like about this response?" : "What was wrong with this response?"}
                                    className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none placeholder:text-slate-400 focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                                />
                            </div>
                            <p className="text-xs text-slate-400">
                                Want to tell us more?{" "}
                                <button type="button" onClick={goToDetailedSurvey} className="font-medium text-violet-600 underline hover:text-violet-700">Take the detailed survey</button>
                            </p>
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" onClick={closeFeedbackDialog} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                            <button
                                type="button"
                                onClick={submitFeedbackDialog}
                                disabled={isSubmittingFeedback}
                                className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40 ${feedbackDialog.isPositive ? "bg-violet-600 hover:bg-violet-700" : "bg-rose-600 hover:bg-rose-700"}`}
                            >
                                Submit feedback
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </Layout>
    );
};

export default Aura;
